"use client"

import React from "react"
import { motion } from "framer-motion"
import { Mail, MousePointer2, Eye, Ban, CheckCircle } from "lucide-react"

interface ActivityItemProps {
  type: string
  title: string
  status: string
  timestamp: string
  index: number
}

const getEventIcon = (type: string) => {
  switch (type.toLowerCase()) {
    case "open": return Eye
    case "click": return MousePointer2
    case "bounce": return Ban
    case "delivery": return CheckCircle
    default: return Mail
  }
}

const getEventStyles = (type: string) => {
  switch (type.toLowerCase()) {
    case "open": return "text-purple-400 bg-purple-500/10 border-purple-500/20 shadow-[0_0_15px_rgba(168,85,247,0.1)]"
    case "click": return "text-emerald-400 bg-emerald-500/10 border-emerald-500/20 shadow-[0_0_15px_rgba(16,185,129,0.1)]"
    case "bounce": return "text-rose-400 bg-rose-500/10 border-rose-500/20 shadow-[0_0_15px_rgba(244,63,94,0.1)]"
    case "delivery": return "text-blue-400 bg-blue-500/10 border-blue-500/20 shadow-[0_0_15px_rgba(59,130,246,0.1)]"
    default: return "text-indigo-400 bg-indigo-500/10 border-indigo-500/20 shadow-[0_0_15px_rgba(99,102,241,0.1)]"
  }
}

const ActivityItem = ({ type, title, status, timestamp, index }: ActivityItemProps) => {
  const Icon = getEventIcon(type)
  const styles = getEventStyles(type)

  return (
    <motion.div
      initial={{ opacity: 0, x: 20 }}
      animate={{ opacity: 1, x: 0 }}
      transition={{ duration: 0.4, delay: index * 0.1 }}
      className="flex items-start gap-4 p-4 rounded-2xl border border-white/5 bg-white/[0.02] transition-colors hover:bg-white/[0.05] group"
    >
      <div className={`p-2 rounded-xl border ${styles}`}>
        <Icon className="h-4 w-4" />
      </div>
      <div className="flex-1">
        <div className="flex items-center justify-between">
          <h4 className="text-sm font-bold text-slate-200 group-hover:text-white transition-colors tracking-tight">{type}</h4>
          <span className="text-[10px] font-bold text-slate-500 uppercase tracking-widest">{timestamp}</span>
        </div>
        <p className="text-xs text-slate-400 font-medium mt-0.5 line-clamp-1">{title}</p>
        <div className="flex items-center gap-1.5 mt-2">
          <div className={`h-1.5 w-1.5 rounded-full ${styles.split(' ')[1].replace('/10', '')}`} />
          <span className="text-[10px] font-bold text-slate-500 uppercase tracking-widest">{status}</span>
        </div>
      </div>
    </motion.div>
  )
}

interface RecentActivityFeedProps {
  activities: Array<{ id: string; type: string; title: string; status: string; timestamp: string }>
}

const RecentActivityFeed = ({ activities }: RecentActivityFeedProps) => {
  return (
    <motion.div
      initial={{ opacity: 0, scale: 0.98 }}
      animate={{ opacity: 1, scale: 1 }}
      transition={{ duration: 0.6 }}
      className="p-6 rounded-3xl border border-white/10 bg-slate-900/40 backdrop-blur-3xl h-full flex flex-col"
    >
      <div className="mb-6 flex items-center justify-between">
        <div>
          <h3 className="text-lg font-bold text-white tracking-tight">Recent Activity</h3>
          <p className="text-xs text-slate-500 font-semibold uppercase tracking-widest mt-1">Live tracking updates</p>
        </div>
        <div className="h-2 w-2 rounded-full bg-emerald-500 animate-pulse-slow shadow-[0_0_10px_rgba(16,185,129,0.5)]" />
      </div>

      <div className="flex-1 space-y-3 overflow-y-auto pr-2 custom-scrollbar max-h-[450px]">
        {activities && activities.length > 0 ? (
          activities.map((activity, index) => (
            <ActivityItem key={activity.id} {...activity} index={index} />
          ))
        ) : (
          <div className="h-40 flex flex-col items-center justify-center text-slate-600 gap-2">
            <Mail className="h-8 w-8 opacity-20" />
            <p className="text-[10px] font-bold uppercase tracking-widest">No recent activity detected</p>
          </div>
        )}
      </div>
    </motion.div>
  )
}

export default RecentActivityFeed
